import { useEffect, useMemo, useState, type MouseEvent, type ReactNode } from "react";
import { Icon, type IconName } from "./icons.js";
import { NAV_GROUPS, type NavGroupId, type ScreenDef } from "./screens.js";

/** What is open over the screen: a group's screens, the account, or nothing. */
export type NavPanel = NavGroupId | "account" | null;

interface Props {
  /** The screens this person may use, in registry order. */
  readonly screens: readonly ScreenDef[];
  /** The screen showing. */
  readonly current: string;
  readonly homeKind: string;
  readonly hrefFor: (kind: string) => string;
  readonly onGo: (kind: string) => void;
  /** The organisation's name and the venue, for the sidebar's head. */
  readonly brand: string;
  readonly place: string;
  /** Who is signed in and where: switchers, theme, sign out. */
  readonly account: ReactNode;
  readonly panel: NavPanel;
  readonly onPanel: (panel: NavPanel) => void;
  /** Fold the sidebar to the rail. Only offered where the sidebar is shown. */
  readonly onCollapse?: (() => void) | undefined;
  /** Unfold the rail back to the sidebar: a desktop whose sidebar was folded. */
  readonly onExpand?: (() => void) | undefined;
}

/** A plain left click, as opposed to one asking for a new tab or window. */
function plainClick(e: MouseEvent): boolean {
  return e.button === 0 && !e.metaKey && !e.ctrlKey && !e.shiftKey && !e.altKey;
}

const OPEN_KEY = "baros.navGroupsOpen";

function readOpen(): Set<NavGroupId> {
  try {
    const raw = window.localStorage.getItem(OPEN_KEY);
    const ids = raw ? (JSON.parse(raw) as unknown) : null;
    return new Set(Array.isArray(ids) ? (ids.filter((i) => typeof i === "string") as NavGroupId[]) : []);
  } catch {
    return new Set();
  }
}

/**
 * The menu, in three shapes for three widths (theme.css decides which shows):
 *
 *   desktop  a sidebar with a heading per group, each folding away — or,
 *            collapsed by the person, the tablet's rail
 *   tablet   a labelled rail — home and the five groups — where a group
 *            opens a panel of its screens beside the rail
 *   phone    the same six along the bottom, a group rising as a sheet
 *
 * The tablet and phone shapes are one piece of markup, `.navbar`, laid out
 * two ways. Grouped because fifteen screens in one list is a list nobody
 * reads at 11pm; the groups are `NAV_GROUPS`, and a screen may be in more
 * than one.
 */
export function Nav(p: Props) {
  const byKind = useMemo(() => new Map(p.screens.map((s) => [s.kind, s])), [p.screens]);
  const groups = useMemo(
    () =>
      NAV_GROUPS.map((g) => ({
        ...g,
        items: g.kinds.map((k) => byKind.get(k)).filter((s): s is ScreenDef => s !== undefined),
      })).filter((g) => g.items.length > 0),
    [byKind],
  );
  const home = byKind.get(p.homeKind);

  /**
   * The group the screen showing is in. A screen in two groups belongs to
   * the one it was opened from, so Recipes opened from Development keeps
   * Development lit rather than jumping to Service.
   */
  const [via, setVia] = useState<NavGroupId | null>(null);
  const containing = groups.filter((g) => g.kinds.includes(p.current)).map((g) => g.id);
  const activeGroup: NavGroupId | null =
    via && containing.includes(via) ? via : (containing[0] ?? null);

  // Which sidebar groups are unfolded, remembered per browser.
  const [open, setOpen] = useState<Set<NavGroupId>>(readOpen);
  /**
   * Arriving at a screen unfolds the group it is in, so the highlighted line
   * is visible.
   *
   * On arrival, and only on arrival: `open` must stay out of the
   * dependencies. With it in, folding the active group by hand put `open`
   * through this effect again, which found the group missing and put it
   * straight back — so the one group a person is most likely to fold, the
   * one they are working in, was the one that would not fold. Reported as
   * the Service group ignoring its dash.
   *
   * The updater reads the set it is handed rather than the one in this
   * closure, so leaving `open` out cannot go stale, and returning the same
   * set when it is already unfolded saves a render and a write to storage.
   */
  useEffect(() => {
    if (!activeGroup) return;
    setOpen((o) => (o.has(activeGroup) ? o : new Set(o).add(activeGroup)));
  }, [activeGroup]);
  useEffect(() => {
    try {
      window.localStorage.setItem(OPEN_KEY, JSON.stringify([...open]));
    } catch {
      // Storage blocked: the sidebar simply forgets between visits.
    }
  }, [open]);

  // Escape shuts whatever is open over the screen.
  const { panel, onPanel } = p;
  useEffect(() => {
    if (!panel) return;
    const onKey = (e: KeyboardEvent) => e.key === "Escape" && onPanel(null);
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [panel, onPanel]);

  const link = (s: ScreenDef, group: NavGroupId | null, className: string) => (
    // Links rather than buttons, so a screen can be opened in a new tab or
    // copied; a plain click stays in the app.
    <a
      key={`${group ?? "home"}:${s.kind}`}
      className={className}
      href={p.hrefFor(s.kind)}
      aria-current={p.current === s.kind ? "page" : undefined}
      onClick={(e) => {
        if (!plainClick(e)) return;
        e.preventDefault();
        setVia(group);
        p.onGo(s.kind);
        p.onPanel(null);
      }}
    >
      <span className="ico"><Icon name={s.icon} /></span>
      <span className="nav-label">{s.label}</span>
    </a>
  );

  const panelGroup = groups.find((g) => g.id === p.panel);

  const barItem = (id: string, icon: IconName, label: string, current: boolean, expanded: boolean, onClick: () => void) => (
    <button
      key={id}
      type="button"
      className="navbar-item"
      aria-current={current ? "true" : undefined}
      aria-expanded={expanded}
      onClick={onClick}
    >
      <Icon name={icon} size={20} />
      <span>{label}</span>
    </button>
  );

  return (
    <>
      <aside className="sidebar">
        <div className="side-brand">
          <div className="side-brand-text">
            <span className="brand">{p.brand}</span>
            <span className="label">{p.place}</span>
          </div>
          {p.onCollapse && (
            <button type="button" className="btn sm side-collapse" title="Collapse the menu" aria-label="Collapse the menu" onClick={p.onCollapse}>
              «
            </button>
          )}
        </div>
        <nav className="sidenav" aria-label="Screens">
          {home && link(home, null, "nav-link")}
          {groups.map((g) => {
            const unfolded = open.has(g.id);
            return (
              <section key={g.id} className="nav-group">
                <button
                  type="button"
                  className="nav-group-head"
                  aria-expanded={unfolded}
                  aria-current={activeGroup === g.id ? "true" : undefined}
                  onClick={() =>
                    setOpen((o) => {
                      const next = new Set(o);
                      if (!next.delete(g.id)) next.add(g.id);
                      return next;
                    })
                  }
                >
                  <span className="ico"><Icon name={g.icon} /></span>
                  <span className="nav-label">{g.label}</span>
                  <span className="chev" aria-hidden="true">{unfolded ? "−" : "+"}</span>
                </button>
                {unfolded && <div className="nav-group-items">{g.items.map((s) => link(s, g.id, "nav-link sub"))}</div>}
              </section>
            );
          })}
        </nav>
        <div className="side-foot">{p.account}</div>
      </aside>

      <nav className="navbar" aria-label="Sections">
        {home &&
          barItem("home", home.icon, "Home", p.current === home.kind, false, () => {
            setVia(null);
            p.onGo(home.kind);
            p.onPanel(null);
          })}
        {groups.map((g) =>
          barItem(g.id, g.icon, g.label, activeGroup === g.id && p.current !== p.homeKind, p.panel === g.id, () =>
            p.onPanel(p.panel === g.id ? null : g.id),
          ),
        )}
        {p.onExpand && (
          <button type="button" className="navbar-expand" title="Expand the menu" aria-label="Expand the menu" onClick={p.onExpand}>
            »
          </button>
        )}
      </nav>

      {p.panel && (
        <>
          <button className="nav-scrim" aria-label="Close" onClick={() => p.onPanel(null)} />
          <div className="nav-panel" role="dialog" aria-label={panelGroup?.label ?? "Account"}>
            <div className="nav-panel-head">
              <span className="label">{panelGroup?.label ?? `${p.brand} · ${p.place}`}</span>
              <span className="spacer" />
              <button className="btn sm" aria-label="Close" onClick={() => p.onPanel(null)}>
                <Icon name="close" size={16} />
              </button>
            </div>
            {panelGroup ? (
              <div className="nav-panel-items">{panelGroup.items.map((s) => link(s, panelGroup.id, "nav-link big"))}</div>
            ) : (
              <div className="nav-panel-account">{p.account}</div>
            )}
          </div>
        </>
      )}
    </>
  );
}
